<!-- AddAiDetachedNoteForm.vue -->
<!--
  Componente AUTÔNOMO para criação de notas avulsas com IA (sem trecho selecionado).
  Cópia independente de AddAiNoteForm.vue com as seguintes diferenças:
    1. Visibilidade controlada por prop `show` / emit `close` — igual a
       AddDetachedNoteForm. NÃO usa inject("isModalHidden"); cria o computed
       localmente e faz provide() para os filhos que precisarem.
    2. contextWindowSize fixo em 0 — a IA recebe o texto integral.
       O campo "Contexto ao redor" não existe; em seu lugar há um aviso informativo.
    3. Coluna direita removida — não há trecho em notas avulsas.
    4. Header e botão de submit exibem "Criar Nota Avulsa com IA".
  NÃO importa nem modifica AddAiNoteForm.vue.
-->
<script setup>
import {
  ref,
  watch,
  computed,
  provide,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

import AddTagForm from "../Tag/AddTagForm.vue";
import AddPermissionForm from "../Generics/AddPermissionForm.vue";
import AddTextLimitModal from "../Text/AddTextLimitModal.vue";
import UpgradePage from "@/components/User/UpgradePage.vue";
import TagCardModal from "@/components/Tag/TagCardModal.vue";
import { useDraggableModal } from "@/composables/useDraggableModal";

import { useTagStore } from "@/stores/useTagStore";
import { useNoteStore } from "@/stores/useNoteStore";
import { useUserStore } from "@/stores/useUserStore";
import { usePermissionStore } from "@/stores/usePermissionStore";
import {
  buildManagerPermissionGrants,
  getDefaultAccessMode,
} from "@/utils/permissionDefaults";
import { useGroupStore } from "@/stores/useGroupStore";

// ====================================================================
// PROPS / EMIT — visibilidade gerenciada aqui, não via inject
// ====================================================================
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  activityId:      Number,
  createdByUserId: Number,
});

const emit = defineEmits(["close"]);

// ====================================================================
// CONTROLE DE VISIBILIDADE (padrão de AddDetachedNoteForm)
// internalHidden espelha !props.show e é atualizado pelo watcher.
// isModalHidden é fornecido via provide() para compatibilidade com
// subcomponentes que possam fazer inject (nenhum neste caso, mas
// mantém o padrão consistente com o restante do projeto).
// ====================================================================
const internalHidden = ref(!props.show);

watch(
  () => props.show,
  (newVal) => {
    internalHidden.value = !newVal;
  }
);

const isModalHidden = computed({
  get() {
    return internalHidden.value;
  },
  set(val) {
    internalHidden.value = val;
    if (val === true) {
      emit("close");
    }
  },
});

// Fornece isModalHidden para subcomponentes (padrão do projeto)
provide("isModalHidden", isModalHidden);
// selectedText vazio — nota avulsa não tem trecho selecionado
provide("selectedText", ref(""));
// registerMouseUpListener no-op — não há TextWithNotes aqui
provide("registerMouseUpListener", () => {});

// ====================================================================
// ESTADOS DE PERSISTÊNCIA
// ====================================================================
const lastUsedTags              = ref([]);
const lastUsedPermissionsMode   = ref("public");
const lastUsedCustomPermissions = ref([]);

const showPlanCatalog    = ref(false);
const showTextLimitModal = ref(false);
const textLimitInfo      = ref({
  limit: null, currentUsage: null, newUsage: null, message: "",
});

const permissionFormKey = ref(0);
const permissionFormRef = ref(null);
const isLoading         = ref(true);

// ====================================================================
// STORES
// ====================================================================
const tagStore        = useTagStore();
const noteStore       = useNoteStore();
const userStore       = useUserStore();
const permissionStore = usePermissionStore();
const groupStore      = useGroupStore();

// ====================================================================
// ESTADO DO FORMULÁRIO
// ====================================================================
const selectedTag      = ref([]);
const showAddTagModal  = ref(false);
const allTags          = ref([]);
const refTitle         = ref(null);

const title    = ref("");
const aiPrompt = ref("");
const source   = ref("");

// contextWindowSize FIXO em 0 — backend entrega o texto integral à IA.
// Não existe ref nem campo editável para este valor neste componente.
const CONTEXT_WINDOW_SIZE = 0;

const permissions = ref([]);

const showTagCardModal      = ref(false);
const selectedTagIdForModal = ref(null);

function openTagCard(tagOption) {
  if (!tagOption?.value) return;
  selectedTagIdForModal.value = tagOption.value;
  showTagCardModal.value = true;
}
function closeTagCard() {
  showTagCardModal.value = false;
  selectedTagIdForModal.value = null;
}

// ====================================================================
// TOAST
// ====================================================================
const showToast      = ref(false);
const toastMessage   = ref("");
const toastTimeoutId = ref(null);
const toastType      = ref("error");

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 2000);
}
function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}
function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

// ====================================================================
// MODAIS AUXILIARES
// ====================================================================
function handleCloseLimitModal()  { showTextLimitModal.value = false; }
function handleOpenPlans()        { showTextLimitModal.value = false; showPlanCatalog.value = true; }
function handleClosePlanCatalog() { showPlanCatalog.value = false; }

// ====================================================================
// HELPERS DE PERMISSÃO
// ====================================================================
const loggedUser    = computed(() => userStore.currentUser);
const creatorUserId = computed(
  () => loggedUser.value?.userId ?? props.createdByUserId ?? null
);
const creatorUserName = computed(() => loggedUser.value?.userName ?? "");

function getTodosGroupId() {
  // Preferência: o "Todos" DO CÍRCULO — grupo mantido pelo sistema com o titular
  // e seus membros ativos. Sem círculo (usuário individual), cai no grupo global
  // da plataforma, como antes.
  const circleDefault = groupStore.groups.find(
    (g) => g.isCircleDefault === true
  );
  if (circleDefault) return circleDefault.groupId ?? null;

  return groupStore.groups.find(
    (g) => g.groupName?.toLowerCase() === "todos"
  )?.groupId ?? null;
}
function getPermissionIdByName(name) {
  return permissionStore.permissions.find(
    (p) => p.permissionName?.toLowerCase() === name.toLowerCase()
  )?.permissionId ?? null;
}

function buildPublicPermissions() {
  const todosId      = getTodosGroupId();
  const creatorId    = creatorUserId.value;
  const visualizarId = getPermissionIdByName("visualizar");
  const selecionarId = getPermissionIdByName("selecionar");
  const editarId     = getPermissionIdByName("editar");
  const perms = [];
  if (todosId && visualizarId && selecionarId) {
    perms.push(
      { entityId: todosId, type: "group", permissionId: visualizarId },
      { entityId: todosId, type: "group", permissionId: selecionarId }
    );
  }
  if (creatorId && visualizarId && selecionarId && editarId) {
    perms.push(
      { entityId: creatorId, type: "user", permissionId: visualizarId },
      { entityId: creatorId, type: "user", permissionId: selecionarId },
      { entityId: creatorId, type: "user", permissionId: editarId }
    );
  }
  // Contexto de membro gerenciado: o titular acompanha o criador em tudo
  // que o membro publica (ver utils/permissionDefaults.js).
  perms.push(
    ...buildManagerPermissionGrants({ visualizarId, selecionarId, editarId })
  );

  return perms;
}

// Molde fechado: só o criador e, em contexto de membro gerenciado, o titular.
// Estes formulários não têm o botão "Privado" — este molde existe para o membro
// gerenciado, cuja nota nasce visível apenas para ele e para o titular.
function buildPrivatePermissions() {
  const creatorId = creatorUserId.value;
  const visualizarId = getPermissionIdByName("visualizar");
  const selecionarId = getPermissionIdByName("selecionar");
  const editarId = getPermissionIdByName("editar");
  if (!creatorId || !visualizarId || !selecionarId || !editarId) return [];
  return [
    { entityId: creatorId, type: "user", permissionId: visualizarId },
    { entityId: creatorId, type: "user", permissionId: selecionarId },
    { entityId: creatorId, type: "user", permissionId: editarId },
    ...buildManagerPermissionGrants({ visualizarId, selecionarId, editarId }),
  ];
}

// Molde com que o formulário abre. Membro gerenciado começa fechado; para os
// demais o padrão histórico continua sendo público.
function buildInitialPermissions() {
  return getDefaultAccessMode() === "private"
    ? buildPrivatePermissions()
    : buildPublicPermissions();
}

function isNoteFullyPublic(perms) {
  const todosId = getTodosGroupId();
  if (!todosId) return false;
  return perms.some((p) => p.type === "group" && p.entityId === todosId);
}

const includesTodosGroup = computed(() =>
  isNoteFullyPublic(permissions.value || [])
);

const permissionMode = ref("public");

const addNotePermissions = computed({
  get() {
    const perms = permissions.value || [];
    if (perms.length === 0) return permissionMode.value;
    return isNoteFullyPublic(perms) ? "public" : "custom";
  },
  set(newValue) {
    permissionMode.value = newValue;
    if (newValue === "public") permissions.value = buildPublicPermissions();
  },
});

const showPermissionModal = ref(false);
const permissionBackup    = ref([]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. O componente fica montado e só
// alterna isModalHidden, então o watch daquele ref recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

// 🔹 NOVO: arrasto do mini-modal de permissões pelo seu cabeçalho.
// O v-if recria o DOM a cada abertura, mas o offset vive aqui — daí o reset.
const {
  panelRef: permPanelRef,
  panelStyle: permPanelStyle,
  isDragging: isPermDragging,
  isDraggable: isPermDraggable,
  onHandlePointerDown: onPermHandlePointerDown,
  resetPosition: resetPermPosition,
} = useDraggableModal();

watch(showPermissionModal, (val) => {
  if (val) resetPermPosition();
});

function openPermissionModal() {
  permissionBackup.value = (permissions.value || []).map((p) => ({ ...p }));
  addNotePermissions.value = "custom";
  if ((!permissions.value || permissions.value.length === 0) && lastUsedCustomPermissions.value.length > 0) {
    permissions.value = [...lastUsedCustomPermissions.value];
  }
  if (!permissions.value || permissions.value.length === 0) {
    permissions.value = buildPublicPermissions();
  }
  showPermissionModal.value = true;
}
function cancelPermissionModal() {
  permissions.value = (permissionBackup.value || []).map((p) => ({ ...p }));
  showPermissionModal.value = false;
}
function confirmPermissionModal() {
  if (
    permissionFormRef.value &&
    typeof permissionFormRef.value.validateBeforeConfirm === "function" &&
    !permissionFormRef.value.validateBeforeConfirm()
  ) return;
  showPermissionModal.value = false;
}

// ====================================================================
// COMPUTED PARA LISTAS DOS FILHOS
// ====================================================================
const userListForChild = computed(() =>
  (userStore.users || []).map((u) => ({ id: u.userId, name: u.userName }))
);
const groupListForChild = computed(() =>
  (groupStore.groups || []).map((g) => ({
    id: g.groupId,
    name: g.groupName,
    createdByUserId:   g.createdByUserId   ?? g.CreatedByUserId   ?? null,
    createdByUserName: g.createdByUserName ?? g.CreatedByUserName ?? null,
    // Membros do grupo: o AddPermissionForm usa para avisar quando a linha de
    // um usuário substitui a permissão que ele teria pelo grupo (precedência).
    members: g.users ?? g.Users ?? [],
  }))
);
const permissionListForChild = computed(() =>
  (permissionStore.permissions || []).map((p) => ({
    id:   p.permissionId,
    name: p.permissionName,
  }))
);

// ====================================================================
// TAGS
// ====================================================================
function isSysEntity(entity) {
  return (
    (entity?.createdByUserName ?? entity?.CreatedByUserName ?? "")
      .toString().trim().toLowerCase() === "sys"
  );
}

function atualizarSubtipos() {
  allTags.value = tagStore.tags.map((sub) => ({
    label: (sub?.tagName || "").trim(),
    value: sub.tagId,
    isSys: isSysEntity(sub),
  }));
}

const newTagName        = ref("");
const multiselectTagKey = ref(0);

function addNewTag(newTag) {
  newTagName.value = newTag;
  showAddTagModal.value = true;
}

async function handleTagCreated(created) {
  const tagIds = selectedTag.value.map((t) => t.value);
  if (!tagIds.includes(created.tagId)) tagIds.push(created.tagId);
  await tagStore.fetchTags();
  atualizarSubtipos();
  selectedTag.value = tagIds
    .map((id) => allTags.value.find((s) => s.value === id))
    .filter(Boolean);
  showAddTagModal.value = false;
}

// ====================================================================
// WATCHER — reage à abertura do modal (isModalHidden passa a false)
// ====================================================================
watch(isModalHidden, async (hidden) => {
  if (hidden === false) {
    // 🔹 NOVO: o modal reabre sem desmontar — recentraliza o painel arrastado
    resetPosition();
    selectedTag.value    = [...lastUsedTags.value];
    permissionMode.value = lastUsedPermissionsMode.value || getDefaultAccessMode();
    if (permissionMode.value === "public") {
      permissions.value = buildPublicPermissions();
    } else if (permissionMode.value === "private") {
      permissions.value = buildPrivatePermissions();
    } else if (permissionMode.value === "custom" && lastUsedCustomPermissions.value.length > 0) {
      permissions.value = [...lastUsedCustomPermissions.value];
    } else {
      permissions.value = [];
    }
    title.value    = "";
    aiPrompt.value = "";
    source.value   = "";
    await nextTick();
    refTitle.value?.focus();
  }
});

// ====================================================================
// FECHAR MODAL
// ====================================================================
const isSubmitting = ref(false);

function closeModal() {
  isSubmitting.value = false;
  lastUsedTags.value = [...selectedTag.value];
  const currentMode = addNotePermissions.value || permissionMode.value;
  lastUsedPermissionsMode.value = currentMode;
  if (currentMode === "custom" && permissions.value?.length) {
    lastUsedCustomPermissions.value = [...permissions.value];
  }
  if (toastTimeoutId.value) {
    clearTimeout(toastTimeoutId.value);
    toastTimeoutId.value = null;
  }
  showToast.value           = false;
  isModalHidden.value       = true;  // dispara emit("close") via computed setter
  title.value               = "";
  aiPrompt.value            = "";
  source.value              = "";
  permissions.value         = [];
  selectedTag.value         = [];
  permissionMode.value      = "public";
  showPermissionModal.value = false;
  permissionFormKey.value++;
}

// ====================================================================
// SUBMISSÃO
// ====================================================================
async function criarComIA() {
  if (isSubmitting.value) return;

  const child = permissionFormRef.value;
  if (child && typeof child.validateBeforeConfirm === "function") {
    const isOk = child.validateBeforeConfirm();
    if (!isOk) {
      showPermissionModal.value = true;
      return;
    }
  }

  isSubmitting.value = true;

  if (addNotePermissions.value === "public") {
    if (!permissions.value || permissions.value.length === 0) {
      permissions.value = buildPublicPermissions();
    }
  }

  try {
    const payload = {
      activityId:        props.activityId,
      textRangeStart:    0,
      textRangeEnd:      0,
      tagIds:            selectedTag.value.map((t) => t.value),
      title:             title.value.trim()    || null,
      source:            source.value.trim()   || null,
      prompt:            aiPrompt.value.trim() || null,
      contextWindowSize: CONTEXT_WINDOW_SIZE,
      permissions:       permissions.value,
    };

    await noteStore.createNoteWithAi(payload, props.activityId);
    await noteStore.setNoteFilters({
      includeOwn:           noteStore.filterIncludeOwn,
      comments:             noteStore.filterIncludeComments,
      sortBy:         noteStore.filterSortOrder,
      includeSelectedUsers: noteStore.filterIncludeSelectedUsers,
    });

    showSuccessToast("Nota criada com IA com sucesso.");
    setTimeout(() => { closeModal(); }, 2000);
  } catch (error) {
    if (
      error.response?.status === 403 ||
      error.response?.data?.message?.includes("limite")
    ) {
      textLimitInfo.value = {
        limit:        error.response?.data?.limit        || null,
        currentUsage: error.response?.data?.currentUsage || null,
        newUsage:     error.response?.data?.newUsage     || null,
        message:      error.response?.data?.message      || "Limite de caracteres atingido",
      };
      showTextLimitModal.value = true;
      isSubmitting.value = false;
      return;
    }

    const msg =
      error.response?.data?.message ||
      error.message ||
      "Ocorreu um erro desconhecido ao criar a nota.";
    showErrorToast(msg);
    isSubmitting.value = false;
  }
}

// ====================================================================
// LIFECYCLE
// ====================================================================
onMounted(async () => {
  isLoading.value = true;
  try {
    await Promise.all([
      tagStore.fetchTags(),
      permissionStore.fetchAllPermissions(),
      groupStore.fetchGroups(1, 1000),
      userStore.fetchUsers(1, 1000),
    ]);
    atualizarSubtipos();
    permissions.value = buildInitialPermissions();
    title.value       = "";
    aiPrompt.value    = "";
    source.value      = "";
  } finally {
    isLoading.value = false;
  }
});

onBeforeUnmount(() => {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});
</script>

<template>
  <div v-if="!isModalHidden" class="modal-backdrop" @click.self="!isSubmitting">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">

      <!-- ── Header ──────────────────────────────────────────────────── -->
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <div class="modal-header-left">
          <span class="ai-badge">
            <i class="fas fa-robot"></i>
          </span>
          <h3 class="modal-title">Criar Nota Avulsa com IA</h3>
        </div>
        <button
          @click="closeModal"
          :disabled="isSubmitting"
          type="button"
          class="modal-close-btn"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414
                 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586
                 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>

      <!-- ── Form ────────────────────────────────────────────────────── -->
      <form
        id="add-ai-detached-note-form"
        @submit.prevent="criarComIA"
        class="modal-form"
        novalidate
      >
        <div class="modal-body">

          <!-- Loading overlay -->
          <transition name="loading-fade">
            <div v-if="isLoading" class="loading-overlay loading-overlay--translucent" aria-label="Carregando...">
              <svg class="loading-spinner" viewBox="0 0 24 24" fill="none">
                <circle class="opacity-25" cx="12" cy="12" r="10"
                  stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
              </svg>
              <span class="loading-text">Carregando...</span>
            </div>
          </transition>

          <!-- Coluna única — nota avulsa não tem coluna direita -->
          <div v-if="!isLoading" class="note-single-col">
            <div class="step-content note-step-top">

              <!-- Tag -->
              <div class="form-group">
                <label for="aid-subtype" class="form-label">Tag (opcional)</label>
                <Multiselect
                  v-model="selectedTag"
                  :options="allTags"
                  :multiple="true"
                  placeholder="Selecione ou crie tags para a nota"
                  label="label"
                  track-by="value"
                  :taggable="true"
                  tag-placeholder="Tecle enter ou clique aqui para criar uma tag"
                  select-label="Tecle enter para selecionar"
                  deselect-label="Tecle enter para remover"
                  selected-label="Selecionado"
                  :allow-empty="true"
                  @tag="addNewTag"
                  :disabled="isSubmitting"
                  :key="multiselectTagKey"
                >
                  <template #option="{ option }">
                    <div class="ms-option-row">
                      <span class="ms-option-text">{{ option.label }}</span>
                      <span v-if="option.isSys" class="sys-badge">SYS</span>
                    </div>
                  </template>
                  <template #tag="{ option, remove }">
                    <span class="ms-chip">
                      <button type="button" class="ms-chip-link"
                        @click.stop="openTagCard(option)">
                        {{ option.label }}
                      </button>
                      <span v-if="option.isSys" class="sys-badge">SYS</span>
                      <button type="button" class="ms-chip-remove"
                        @click.stop="remove(option)" aria-label="Remover">×</button>
                    </span>
                  </template>
                </Multiselect>
              </div>

              <!-- Título -->
              <div class="form-group">
                <label for="aid-title" class="form-label">
                  Título
                  <span class="form-label-hint">(deixe vazio para a IA gerar)</span>
                </label>
                <input
                  ref="refTitle"
                  v-model="title"
                  type="text"
                  id="aid-title"
                  class="input-base"
                  placeholder="A IA irá gerar o título automaticamente"
                  :disabled="isSubmitting"
                  maxlength="100"
                />
              </div>

              <!-- Fonte -->
              <div class="form-group">
                <label for="aid-source" class="form-label">
                  Fonte
                  <span class="form-label-hint">(deixe vazio para a IA sugerir)</span>
                </label>
                <textarea
                  v-model="source"
                  id="aid-source"
                  class="ai-textarea"
                  placeholder="A IA irá sugerir uma fonte ou usar 'Acervo Litterae'"
                  :disabled="isSubmitting"
                  rows="3"
                ></textarea>
              </div>

              <!-- Prompt -->
              <div class="form-group prompt-group">
                <label for="aid-prompt" class="form-label">
                  Prompt
                  <span class="form-label-hint">(instrução para a IA)</span>
                </label>
                <textarea
                  id="aid-prompt"
                  v-model="aiPrompt"
                  class="ai-textarea"
                  placeholder="Ex.: Analise o uso de metáforas neste trecho. Foque no aspecto fonético."
                  :disabled="isSubmitting"
                  rows="4"
                ></textarea>
              </div>

              <!-- Aviso de texto integral (substitui "Contexto ao redor") -->
              <div class="form-group full-text-notice">
                <i class="fas fa-file-alt full-text-notice-icon"></i>
                <span class="full-text-notice-text">
                  A IA analisará o texto integral da atividade.
                </span>
              </div>

              <!-- Permissões -->
              <div class="form-group access-level-group">
                <label class="form-label">Permissões:</label>
                <div class="access-buttons-container">
                  <div
                    class="public-access-indicator"
                    :class="{
 'public-access-indicator--active':   includesTodosGroup,
                      'public-access-indicator--inactive': !includesTodosGroup,
                    }"
                  >
                    <i class="fas fa-globe-americas btn-icon-fa"></i>
                    <span class="btn-text">{{ includesTodosGroup ? "Público" : "Restrito" }}</span>
                  </div>
                  <button type="button" class="btn-access-toggle"
                    :disabled="isSubmitting" @click="openPermissionModal">
                    <i class="fas fa-user-shield btn-icon-fa"></i>
                    <span class="btn-text">Editar permissões</span>
                  </button>
                </div>
              </div>

              <div style="flex-grow: 1"></div>
            </div>
          </div>

        </div>

        <!-- ── Footer ──────────────────────────────────────────────── -->
        <div class="modal-footer">
          <button
            type="submit"
            form="add-ai-detached-note-form"
            class="btn btn-ai"
            :disabled="isSubmitting || isLoading"
          >
            <svg v-if="isSubmitting" class="mr-1 -ml-1 w-5 h-5 animate-spin"
              viewBox="0 0 24 24" fill="none">
              <circle class="opacity-25" cx="12" cy="12" r="10"
                stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
            </svg>
            <i v-else class="fas fa-wand-magic-sparkles mr-1"></i>
            <span>{{ isSubmitting ? "Gerando nota..." : "Criar Nota Avulsa com IA" }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- ── TagCardModal ──────────────────────────────────────────────── -->
  <TagCardModal
    :show="showTagCardModal"
    :tagId="selectedTagIdForModal"
    @close="closeTagCard"
  />

  <!-- ── Modal de permissões ───────────────────────────────────────── -->
  <Teleport to="body">
    <transition name="permission-modal-fade">
      <div v-if="showPermissionModal" class="permission-modal-backdrop">
        <div
          ref="permPanelRef"
          class="permission-modal-panel"
          :style="permPanelStyle"
          @click.stop
        >
          <div
            class="permission-modal-header"
            :class="{
 'permission-modal-header--draggable': isPermDraggable,
              'permission-modal-header--dragging': isPermDragging,
            }"
            @pointerdown="onPermHandlePointerDown"
          >
            <h4 class="permission-modal-title">Editar Permissões</h4>
            <button type="button" class="permission-modal-close"
              @click="cancelPermissionModal" aria-label="Fechar">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414
                     10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586
                     10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>
          </div>
          <div class="permission-modal-body">
            <p class="permission-modal-instruction">
              Defina aqui usuários, grupos e permissões personalizadas para esta nota.
            </p>
            <AddPermissionForm
              ref="permissionFormRef"
              v-model:payload="permissions"
              :key="permissionFormKey"
              :users="userListForChild"
              :groups="groupListForChild"
              :permissions="permissionListForChild"
              entityType="Note"
              :creatorUserName="creatorUserName"
            />
          </div>
          <div class="permission-modal-footer">
            <button type="button" class="btn btn-secondary"
              @click="cancelPermissionModal" :disabled="isSubmitting">Cancelar</button>
            <button type="button" class="btn btn-success"
              @click="confirmPermissionModal" :disabled="isSubmitting">Confirmar</button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>

  <!-- ── Modal de nova tag ─────────────────────────────────────────── -->
  <Teleport to="body">
    <div v-if="showAddTagModal"
      class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/30 p-4"
      @click.self="showAddTagModal = false">
      <div class="w-full max-w-2xl" @click.stop>
        <AddTagForm
          :initialName="newTagName"
          @tag-added="handleTagCreated"
          @close="showAddTagModal = false"
        />
      </div>
    </div>

    <AddTextLimitModal
      :show="showTextLimitModal"
      :info="textLimitInfo"
      entityType="notas"
      @close="handleCloseLimitModal"
      @open-plans="handleOpenPlans"
    />

    <!-- Mesmo fluxo de upgrade do DashboardLayout (catálogo + checkout);
         z-index acima do formulário de nota e do modal de limite -->
    <UpgradePage
      v-if="showPlanCatalog"
      :initial-step="2"
      :redirect-on-catalog-close="false"
      :z-index="10010"
      @close-upgrade-modal="handleClosePlanCatalog"
      @close="handleClosePlanCatalog"
    />

    <!-- Toast -->
    <transition name="toast-fade">
      <div v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4">
        <div :class="['toast pointer-events-auto',
 toastType === 'error' ? 'toast-error' : 'toast-success']">
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle"/>
              <path v-if="toastType === 'error'" d="M10 5v6M10 13.5v1.5"
                stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
              <path v-else d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor" stroke-width="2.2"
                stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">{{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}</p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button type="button" class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false" aria-label="Fechar notificação">x</button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>

.modal-panel {
  width: 50vw;
  max-width: 50vw;
  height: 700px;
  max-height: calc(100vh - 20px - 2rem);
}
.modal-form { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; }

.modal-header-left { display: flex; align-items: center; gap: 0.6rem; }
.ai-badge {
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border-radius: 8px;
  background-color: var(--brand-light); color: #1a1a2e; font-size: 0.8rem; flex-shrink: 0;
}

.modal-footer {
  bottom: 0;
}

/* ── Loading ─────────────────────────────────────────────────────── */

/* ── Layout coluna única ─────────────────────────────────────────── */
.note-single-col { display: flex; flex-direction: column; min-height: 0; flex: 1 1 auto; }
.step-content {
  display: flex; flex-direction: column; gap: 0.75rem;
  overflow-y: auto; padding-right: 0.25rem; height: 100%;
}
.note-step-top { justify-content: flex-start; }

.form-label {
  flex-shrink: 0;
}
.form-label-hint { font-size: 0.75rem; font-weight: 400; color: var(--text-light); }

.prompt-group { flex: 1 1 auto; }
.ai-textarea {
  width: 100%; padding: 0.75rem; border: 1px solid var(--neutral-400); border-radius: 12px;
  resize: vertical; outline: none; font-size: 0.95rem; color: var(--text-dark); background: var(--surface-3);
  transition: box-shadow 0.2s, border-color 0.2s;
}
.ai-textarea:focus { border-color: var(--brand); box-shadow: var(--focus-ring-brand); }
.ai-textarea:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Aviso de texto integral ─────────────────────────────────────── */
.full-text-notice {
  flex-direction: row; align-items: center; gap: 0.5rem;
  background-color: #f0edff; border: 1px solid #c4b5fd;
  border-radius: 8px; padding: 0.4rem 0.75rem; min-height: 32px;
}
.full-text-notice-icon { color: #7c6af5; font-size: 0.85rem; flex-shrink: 0; }
.full-text-notice-text { font-size: 0.82rem; font-weight: 500; color: #7c6af5; }

/* ── Acesso ──────────────────────────────────────────────────────── */
.access-level-group { flex-direction: row; align-items: center; gap: 1rem; }
.access-buttons-container { display: flex; gap: 0.75rem; align-items: center; }
.public-access-indicator {
  display: inline-flex; align-items: center; gap: 0.6rem;
  padding: 0.4rem 1rem; border-radius: 10px;
  border: 1px dashed var(--neutral-400); background-color: var(--surface-2);
  color: var(--text-muted); font-size: 0.8rem; font-weight: 500; cursor: default; user-select: none;
}
.public-access-indicator--active  { border-color: var(--success); background-color: var(--success-bg); color: var(--success-text); }
.public-access-indicator--inactive { border-color: var(--danger); background-color: var(--danger-bg); color: var(--danger-text); }
.btn-access-toggle {
  display: inline-flex; align-items: center; justify-content: center;
  gap: 6px; height: 28px; padding: 0 0.65rem; border-radius: 9px;
  background-color: var(--neutral-200); border: 1px solid var(--border-color);
  color: var(--text-dark); cursor: pointer; font-size: 0.8rem; font-weight: 600;
  transition: all 0.2s ease-in-out;
}
.btn-icon-fa { font-size: 0.9rem; }
.btn-access-toggle:hover:not(:disabled) { background-color: var(--neutral-300); }
.btn-access-toggle:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-ai {
  background: linear-gradient(135deg, var(--brand-light), var(--brand));
  color: #1a1a2e; box-shadow: 0 4px 14px rgba(155,132,255,0.35);
}
.btn-ai:hover:not(:disabled) {
  background: linear-gradient(135deg, #a99cf5, var(--brand-hover));
  box-shadow: 0 6px 18px rgba(155,132,255,0.45); transform: translateY(-1px);
}

/* ── Multiselect chips ───────────────────────────────────────────── */
.ms-option-row  { display: flex; align-items: center; gap: 6px; }
.ms-option-text { flex: 1 1 auto; }
.ms-chip {
  display: inline-flex; align-items: center; gap: 5px; background: var(--surface-3);
  border-radius: 7px; padding: 2px 6px; margin: 2px 4px 2px 0; font-size: 0.82rem;
}
.ms-chip-link { border: none; background: transparent; cursor: pointer; font-size: inherit; color: inherit; padding: 0; }
.ms-chip-link:hover { text-decoration: underline; }
.ms-chip-remove { border: none; background: transparent; cursor: pointer; font-size: 1rem; color: var(--text-muted); padding: 0 2px; }

/* ── Modal permissões ────────────────────────────────────────────── */
.permission-modal-backdrop {
  position: fixed; inset: 0; z-index: 10100; background-color: rgba(15,23,42,0.2);
  display: flex; justify-content: center; align-items: flex-start; padding: 50px 1rem 1rem;
}
.permission-modal-panel {
  width: 590px; max-width: 100%; height: 760px; max-height: calc(100vh - 100px);
  background-color: var(--bg-content); border-radius: 18px; border: 1px solid var(--border-color);
  box-shadow: var(--shadow-modal-lg); display: flex; flex-direction: column; overflow: hidden;
}
.permission-modal-header {
  flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between;
  padding: 0.5rem 1rem; border-bottom: 1px solid var(--border-color); background-color: var(--bg-header);
}
/* 🔹 NOVO: cabeçalho do mini-modal como alça de arrasto.
   touch-action: none entrega o gesto ao pointermove em vez da rolagem. */
.permission-modal-header--draggable {
  cursor: move;
  user-select: none;
  touch-action: none;
}
.permission-modal-header--dragging {
  cursor: grabbing;
}
/* Os botões do cabeçalho continuam sendo clique, não alça */
.permission-modal-header--draggable button {
  cursor: pointer;
}

.permission-modal-title { font-size: 1.05rem; font-weight: 600; color: var(--text-dark); margin: 0; }
.permission-modal-close {
  color: var(--text-light); background-color: transparent; border-radius: 8px; padding: 0.375rem;
  transition: background-color 0.2s, color 0.2s; border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center; width: 32px; height: 32px;
}
.permission-modal-close:hover { background-color: rgba(0,0,0,0.1); color: var(--text-dark); }
.permission-modal-body {
  padding: 0.8rem 1rem 0.5rem; flex: 1 1 auto; display: flex; flex-direction: column;
  background-color: var(--bg-content); min-height: 0; overflow: hidden;
}
.permission-modal-instruction { font-size: 0.82rem; color: var(--text-light); margin-bottom: 0.75rem; line-height: 1.35; display: block; }
.permission-modal-footer {
  flex: 0 0 auto; padding: 0.6rem 1rem 0.75rem; border-top: 1px solid var(--border-color);
  background-color: var(--bg-header); display: flex; justify-content: flex-end; align-items: center; gap: 0.75rem;
}
.permission-modal-fade-enter-active, .permission-modal-fade-leave-active { transition: opacity 0.15s ease, transform 0.18s ease; }
.permission-modal-fade-enter-from, .permission-modal-fade-leave-to { opacity: 0; transform: translateY(8px); }
:deep(.permission-form-wrapper) { display: flex; flex-direction: column; height: 100%; min-height: 0; }
:deep(.mt-5) { flex: 1 1 auto; display: flex; flex-direction: column; min-height: 0; margin-top: 1rem !important; }
:deep(.table-wrap) { flex: 1 1 auto; overflow-y: auto; overflow-x: auto; border-radius: 8px; background: var(--bg-content); margin-top: 0.5rem; }

</style>
